import { useEffect, useState } from 'react';
import { Box, Link as MuiLink, Stack, Typography } from '@mui/material';
import { getApprovedContentById } from '../lib/api/content-api-client';
import { getPublicWebsiteConfig } from '../lib/api/config';
import type { ApprovedContentItem } from '../lib/api/content-api-client';

interface RecentCoverageProps {
  /**
   * Raw wire `articles_mentioned` uuid list from the Game row's
   * DynamoDB StringSet (round-26 R20.6 reverse pointer per
   * `content-ingestion-pipeline` Sub-R5.2). Empty list hides the
   * section per the Sub-R5.2 acceptance criterion "handle empty
   * `articles_mentioned` gracefully".
   */
  readonly articleUuids: ReadonlyArray<string>;
}

/**
 * `<RecentCoverage>` — Game-page "Recent coverage" section per
 * `content-ingestion-pipeline` Sub-R5.2 / R20.6.
 *
 * Fans out one `POST /GetApprovedContentByIdV1` call per uuid in
 * the game row's `articles_mentioned` StringSet, filters out 404
 * responses (news items that were rejected or deleted after the
 * pointer was appended), and renders each surviving News record
 * using the same visual structure as the Daily-page's
 * `<NewsItem>` — heading + floated image + wrapped body + source
 * line — but without the admin edit affordance (this is a
 * consumer-facing view).
 *
 * Empty `articleUuids` renders `null` so the section is hidden
 * cleanly on games with no reverse-pointer entries yet.
 *
 * Function-length budget: every function stays under 20 lines
 * per Requirement 3.7. Rendering uses simple presentational
 * components; no Redux slice is threaded through since the panel
 * data is only consumed on this page.
 */
export function RecentCoverage({ articleUuids }: RecentCoverageProps): JSX.Element | null {
  const items = useRecentCoverageItems(articleUuids);
  if (articleUuids.length === 0) return null;
  if (items === undefined) return <Typography>Loading recent coverage…</Typography>;
  if (items.length === 0) return null;
  return (
    <Box>
      <Typography variant="h2" component="h2" sx={{ mb: 2 }}>Recent coverage</Typography>
      <Stack spacing={3}>
        {items.map((item) => (
          <RecentCoverageItem key={item.id ?? item.title ?? ''} item={item} />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Hook: fan out `getApprovedContentById` across the uuid list,
 * filter out 404 responses, and return the surviving items. The
 * hook returns `undefined` while the fetch is in flight so the
 * caller can render a loading affordance.
 */
function useRecentCoverageItems(
  uuids: ReadonlyArray<string>,
): ReadonlyArray<ApprovedContentItem> | undefined {
  const [items, setItems] = useState<ReadonlyArray<ApprovedContentItem> | undefined>(
    uuids.length === 0 ? [] : undefined,
  );
  useEffect(() => {
    if (uuids.length === 0) {
      setItems([]);
      return;
    }
    let cancelled = false;
    fetchRecentCoverage(uuids).then((next) => { if (!cancelled) setItems(next); });
    return () => { cancelled = true; };
  }, [uuids]);
  return items;
}

/**
 * Fetch every uuid in parallel and drop 404s. Failed fetches
 * (network error, 5xx) also drop out silently so a single flaky
 * response does not blank the entire panel; the caller sees the
 * surviving subset.
 */
async function fetchRecentCoverage(
  uuids: ReadonlyArray<string>,
): Promise<ReadonlyArray<ApprovedContentItem>> {
  const config = getPublicWebsiteConfig();
  const options = { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey };
  const settled = await Promise.allSettled(
    uuids.map((uuid) => getApprovedContentById(uuid, options)),
  );
  const items: ApprovedContentItem[] = [];
  for (const result of settled) {
    if (result.status === 'fulfilled' && result.value !== undefined) items.push(result.value);
  }
  return items;
}

interface RecentCoverageItemProps {
  readonly item: ApprovedContentItem;
}

/**
 * `<RecentCoverageItem>` — one News-record card matching the
 * Daily-page `<NewsItem>` layout (title, floated image, body,
 * source line) so the "Recent coverage" panel renders with visual
 * parity per Sub-R5.2 "Use existing `<NewsItem>` layout for
 * parity".
 *
 * Prefers the round-21 envelope-decomposition first-class fields
 * (`title` / `image` / `sourceUrl`) over the raw `body` JSON so
 * the render surface matches the Daily-page path.
 */
function RecentCoverageItem({ item }: RecentCoverageItemProps): JSX.Element {
  const title = item.title ?? '';
  const image = item.image ?? '';
  const body = item.body ?? '';
  const sourceLabel = item.source ?? '';
  const sourceUrl = item.sourceUrl ?? '';
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {title && <RecentCoverageTitle title={title} date={item.date ?? ''} contentId={item.id ?? ''} />}
      {image && <RecentCoverageImage image={image} title={title} />}
      {body && <Typography variant="body1">{body}</Typography>}
      {sourceLabel && (
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          Read more at{' '}
          {sourceUrl ? (
            <MuiLink href={sourceUrl} rel="noopener noreferrer" target="_blank">
              {sourceLabel}
            </MuiLink>
          ) : (
            sourceLabel
          )}
          .
        </Typography>
      )}
    </Box>
  );
}

interface RecentCoverageTitleProps {
  readonly title: string;
  readonly date: string;
  readonly contentId: string;
}

/**
 * Title line with a deep link to the Daily page anchor that renders
 * this same News record in its published context. Mirrors the
 * `<ArticlesMentioned>` deep-link convention (`/daily/{date}#content-
 * {contentId}`) from `<GamePage>` so users can jump to the day view.
 */
function RecentCoverageTitle({ title, date, contentId }: RecentCoverageTitleProps): JSX.Element {
  const target = date && contentId ? `/daily/${date}#content-${contentId}` : undefined;
  const content = (
    <Typography
      variant="h4"
      component="h3"
      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600, mb: 1.5 }}
    >
      {title}
    </Typography>
  );
  if (!target) return content;
  return (
    <MuiLink href={target} sx={{ textDecoration: 'none', color: 'inherit' }}>
      {content}
    </MuiLink>
  );
}

interface RecentCoverageImageProps {
  readonly image: string;
  readonly title: string;
}

/**
 * Floated image mirroring the Daily-page `<NewsItem>` treatment.
 * The left-float + right-edge mask blends the image into the
 * wrapped body copy on desktop; on mobile the image stacks above
 * the body full-width.
 */
function RecentCoverageImage({ image, title }: RecentCoverageImageProps): JSX.Element {
  return (
    <Box
      component="img"
      src={image}
      alt={title || 'News image'}
      sx={{
        float: { xs: 'none', sm: 'left' },
        width: { xs: '100%', sm: 'auto' },
        height: 'auto',
        maxWidth: { xs: '100%', sm: '50%' },
        maxHeight: { sm: 240 },
        objectFit: 'contain',
        borderRadius: 1,
        mr: { sm: 2 },
        mb: { xs: 1.5, sm: 1 },
        maskImage: 'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,1) 88%, transparent 100%)',
      }}
    />
  );
}
